import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../i18n";
import ProductCard from "../components/ProductCard";

const HERO_IMG = "https://images.unsplash.com/photo-1623018697148-8350cf18e64e?crop=entropy&cs=srgb&fm=jpg&q=85";
const KEEPER_IMG = "https://images.pexels.com/photos/20181686/pexels-photo-20181686.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HIVES_IMG = "https://images.pexels.com/photos/28899990/pexels-photo-28899990.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function MaskedLine({ text, delay }) {
  return (
    <span className="mask-line">
      <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>
        {text}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [courses, setCourses] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data.slice(0, 2))).catch(() => {});
  }, []);

  const chapters = [
    { num: "01", title: t("manifesto.1.title"), desc: t("manifesto.1.desc") },
    { num: "02", title: t("manifesto.2.title"), desc: t("manifesto.2.desc") },
    { num: "03", title: t("manifesto.3.title"), desc: t("manifesto.3.desc") },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] bg-bark text-parchment overflow-hidden grain flex items-end" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover opacity-40" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-bark via-bark/50 to-bark/20" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-40 w-full">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.6 }}
            className="inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-gold border border-gold/30 bg-gold/10 px-3 py-1.5 mb-8" data-testid="hero-batch-badge">
            {t("hero.badge")}
          </motion.p>
          <h1 className="font-serif font-bold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-8xl max-w-4xl" data-testid="hero-heading">
            <MaskedLine text={t("hero.line1")} delay={0.15} />
            <MaskedLine text={t("hero.line2")} delay={0.3} />
            <span className="mask-line"><motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }} className="italic text-gold">{t("hero.line3")}</motion.span></span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-7 max-w-xl text-parchment/70 text-base sm:text-lg leading-relaxed" data-testid="hero-subheadline">
            {t("hero.sub")}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.7 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/shop" data-testid="hero-cta-shop"
              className="group flex items-center gap-2 bg-honey text-bark font-semibold px-7 py-4 hover:bg-gold transition-colors duration-300">
              {t("hero.cta1")} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/corsi" data-testid="hero-cta-courses"
              className="flex items-center gap-2 border border-parchment/30 text-parchment px-7 py-4 hover:border-gold hover:text-gold transition-colors duration-300">
              <GraduationCap size={17} /> {t("hero.cta2")}
            </Link>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-parchment/40">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ArrowDown size={14} /></motion.span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="bg-honey text-bark overflow-hidden py-3.5 border-y border-bark/20" data-testid="editorial-marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((n) => (
            <span key={n} className="font-mono text-xs uppercase tracking-[0.25em] font-medium pr-4">{t("marquee").repeat(3)}</span>
          ))}
        </div>
      </div>

      {/* MANIFESTO */}
      <section id="manifesto" className="max-w-7xl mx-auto px-5 sm:px-8 py-28" data-testid="manifesto-section">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="font-mono text-xs uppercase tracking-[0.25em] text-amber-800 mb-4">{t("manifesto.eyebrow")}</motion.p>
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-2xl leading-tight" data-testid="manifesto-title">
          {t("manifesto.title")}
        </motion.h2>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="space-y-0 divide-y divide-[#E5D9C8]">
            {chapters.map((c, i) => (
              <motion.div key={c.num} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }} className="py-8 flex gap-8 group" data-testid={`manifesto-chapter-${c.num}`}>
                <span className="font-mono text-4xl text-honey/40 group-hover:text-honey transition-colors duration-500 font-medium">{c.num}</span>
                <div>
                  <h3 className="font-display text-2xl font-semibold mb-3">{c.title}</h3>
                  <p className="text-cocoa/70 leading-relaxed">{c.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
            className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border border-honey/40 rounded-sm" />
            <img src={KEEPER_IMG} alt="Apicoltore LoMIELlina" className="relative rounded-sm w-full aspect-[4/5] object-cover shadow-2xl shadow-amber-900/10" />
          </motion.div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-cream/60 py-24" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-amber-800 mb-3">{t("home.featured.eyebrow")}</p>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold" data-testid="featured-title">{t("home.featured.title")}</h2>
            </div>
            <Link to="/shop" data-testid="featured-shop-cta" className="hidden sm:flex items-center gap-2 text-honey font-medium hover:gap-3 transition-all">
              {t("home.featured.cta")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* COURSES TEASER */}
      <section className="relative bg-bark text-parchment py-28 overflow-hidden grain" data-testid="courses-teaser">
        <img src={HIVES_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">{t("home.courses.eyebrow")}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-xl leading-tight">{t("home.courses.title")}</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white/5 backdrop-blur border border-white/10 p-8 hover:border-gold/40 transition-colors duration-500" data-testid={`course-teaser-${c.id}`}>
                <p className="font-mono text-[11px] uppercase tracking-widest text-gold mb-3">{c.level} · {c.duration}</p>
                <h3 className="font-serif text-2xl font-semibold leading-snug">{c.title_it}</h3>
                <div className="mt-5 flex items-center justify-between">
                  <span className="font-mono text-lg text-gold">€{c.price.toFixed(0)}</span>
                  <Link to="/corsi" className="text-sm text-parchment/70 hover:text-gold transition-colors flex items-center gap-1.5">
                    {t("courses.book")} <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          <Link to="/corsi" data-testid="courses-teaser-cta"
            className="mt-12 inline-flex items-center gap-2 border border-gold/50 text-gold px-7 py-4 hover:bg-gold hover:text-bark transition-colors duration-300 font-medium">
            {t("home.courses.cta")} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
